import { useRef, useState } from "react";

import useSalesCoachVersion from "../hooks/sales-coach-version.hook";
import useSalesCoachReviewAccess from "../hooks/use-sales-coach-review-access.hook";

export interface UseScribeSalesCoachTabArgs {
  scribeId: number | undefined;
  /**
   * The caller's own gate: the scribe is COMPLETED and the dialog is not in audiology mode.
   *
   * COMPLETED is the backend's own eligibility rule, so checking it before the request keeps
   * every other opener of that modal request-free and means the tab can never be offered for a
   * recording the server would refuse. Audiology mode is excluded for the same reason the After
   * Visit Summary tab is: it keeps the dialog focused on the report summary it was opened for.
   */
  isEligible: boolean;
}

export interface ScribeSalesCoachTab {
  isVisible: boolean;
  /** `null` while it is resolving, or once it has stopped resolving behind the latch. */
  reviewId: number | null;
}

/**
 * Whether the scribe modal shows a "Sales Coach Review" tab for this recording, and which review
 * it renders.
 *
 * **EXISTENCE gates this tab, not eligibility** — the opposite of the sibling Debrief tab, and
 * deliberately. Debrief was revised to eligibility because it needed somewhere to put a
 * "Generate Debrief" trigger; this tab never starts a first analysis (that belongs on the
 * appointment page), and its one action, Re-analyse, needs a review to exist. A tab that could
 * only ever say "nothing here" is noise.
 *
 * **The latch is the load-bearing part.** `availableDetailTabs` feeds an effect that resets
 * `tabView` to the fallback whenever the current tab disappears — so if a refetch ever stopped
 * resolving the row, dropping the tab would silently yank the reader off the tab they were
 * reading. Once shown for a recording, the tab stays; the panel renders its own "no longer
 * available" state instead.
 */
export default function useScribeSalesCoachTab({
  scribeId,
  isEligible
}: UseScribeSalesCoachTabArgs): ScribeSalesCoachTab {
  const { canView, row } = useSalesCoachReviewAccess({
    scribeId,
    skip: !isEligible || scribeId == null
  });

  const reviewId = canView ? (row?.review_id ?? null) : null;

  // Keyed by recording so the latch cannot leak across scribes in a modal that stays mounted.
  const latchedScribeIdRef = useRef<number | null>(null);
  if (reviewId !== null && scribeId != null) {
    latchedScribeIdRef.current = scribeId;
  }

  /*
   * v2 only: keep the FIRST review id this recording resolved to. The v2 list serves the v1 id
   * until a fresh v2 row exists and the sibling's id after — opening the tab is often what
   * derives it — and the v2 detail route answers either. Following the flip would remount the
   * panel and refetch the review the reader is already looking at. v1 ids never flip, and a
   * row that stops resolving still returns `null`, exactly as before.
   *
   * State set during render, not a ref: React's pattern for remembering a value from an
   * earlier render. On the render that latches, the state is still the old one, so the
   * fallback below returns the id being latched — the same value either way.
   */
  const isV2 = useSalesCoachVersion() === "v2";
  const [firstReview, setFirstReview] = useState<{
    scribeId: number;
    reviewId: number;
  } | null>(null);
  if (
    reviewId !== null &&
    scribeId != null &&
    firstReview?.scribeId !== scribeId
  ) {
    setFirstReview({ scribeId, reviewId });
  }
  const stableReviewId =
    isV2 && reviewId !== null && firstReview?.scribeId === scribeId
      ? firstReview.reviewId
      : reviewId;

  return {
    isVisible: scribeId != null && latchedScribeIdRef.current === scribeId,
    reviewId: stableReviewId
  };
}
